import React, { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Webphone from 'common/Webphone'
import { acceptIncomingCall, minimizeIncomingCall, rejectIncomingCall } from 'store/actions/call'
import { CallKeyboardActions } from 'store/models/CallPreferences'
import { CancelCallButton } from 'components/pages/dialpad/components/CancelCallButton'
import { CallButton } from 'components/pages/dialpad/components/CallButton'
import { visualNumberFormat } from 'utils/numberUtils'
import { useTranslation } from 'react-i18next'
import { MinimizeButton, ShowButton, Marquee } from 'components/shared'
import { ReactComponent as OpenInNewTabIcon } from 'assets/svg/shared/open_in_new_tab.svg'
import { getLeadTitle } from 'common/Helpers'
import { integrationFavicons } from 'common/Enums'

const buttonsProps = {
    width: '40px',
    height: '40px',
    imgHeight: '19px',
    radius: '10px'
}

export default function IncomingCallPopup({ incomingCall, integration = {}, onShowLeadClick }) {
    const { number } = incomingCall

    const dispatch = useDispatch()
    const { t } = useTranslation()

    const { callPreferences } = useSelector((state) => state.settings)

    const onAccept = () => {
        dispatch(acceptIncomingCall({ number }))
    }

    const onReject = () => {
        dispatch(rejectIncomingCall({ number }))
    }

    const onMinimize = () => {
        dispatch(minimizeIncomingCall({ number }))
    }

    useEffect(() => {
        let isPickUpActionEnabled = false
        let isDeclineEnabled = false

        callPreferences.forEach((item) => {
            if (item.type === CallKeyboardActions.pickup && item.isChecked) {
                isPickUpActionEnabled = true
            }
            if (item.type === CallKeyboardActions.decline && item.isChecked) {
                isDeclineEnabled = true
            }
        })

        function onKeyPress(event) {
            const keyName = event.key
            if (isPickUpActionEnabled && keyName === 'Enter') {
                Webphone.acceptCall(number)
            }
            if (isDeclineEnabled && keyName === 'Escape') {
                Webphone.rejectCall(number)
            }
        }

        if (isPickUpActionEnabled || isDeclineEnabled) {
            document.addEventListener('keyup', onKeyPress)
        }

        return () => {
            if (isPickUpActionEnabled || isDeclineEnabled) {
                document.removeEventListener('keyup', onKeyPress)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const [incomingCallNumber, setIncomingCallNumber] = useState('')
    const [incomingCallDisplayName, setIncomingCallDisplayName] = useState('')

    const [incomingCallInsight, setIncomingCallInsight] = useState(false)
    const [incomingCallInsightTitle, setIncomingCallInsightTitle] = useState('')
    const [incomingCallInsightUrl, setIncomingCallInsightUrl] = useState('')
    const [incomingCallInsightLength, setIncomingCallInsightLength] = useState(0)

    const [numberWidth, setNumberWidth] = useState(0)
    const [displayNameWidth, setDisplayNameWidth] = useState(0)
    const [bothFit, setBothFit] = useState(0)

    const refNumber = useRef(null)
    const refDisplayName = useRef(null)
    const refHolder = useRef(null)

    const refNumberOffsetWidth = refNumber.current?.offsetWidth
    const refDisplayNameOffsetWidth = refDisplayName.current?.offsetWidth

    useEffect(() => {
        if (incomingCall?.number) {
            setIncomingCallNumber(incomingCall.number)
        }
        if (incomingCall?.displayName && incomingCall.displayName !== incomingCall.number) {
            setIncomingCallDisplayName(incomingCall.displayName)
        }

        if (incomingCall?.callerInsights) {
            const { records, record, search_url: searchUrl } = incomingCall.callerInsights
            const contacts = records || record

            if (contacts.length === 1) {
                setIncomingCallInsightTitle(getLeadTitle(contacts[0].properties))
                setIncomingCallInsightUrl(contacts[0].url)
            } else {
                setIncomingCallInsightTitle(`${contacts.length} ${t('records_found')}`)
                setIncomingCallInsightUrl(searchUrl)
            }

            setIncomingCallInsightLength(contacts.length)
            setIncomingCallInsight(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [incomingCall])

    useEffect(() => {
        refNumber.current?.offsetWidth && setNumberWidth(refNumber.current.offsetWidth)
    }, [refNumberOffsetWidth])

    useEffect(() => {
        refDisplayName.current?.offsetWidth && setDisplayNameWidth(refDisplayName.current.offsetWidth)
    }, [refDisplayNameOffsetWidth])

    useEffect(() => {
        const handleResize = () => {
            setBothFit(displayNameWidth + numberWidth < refHolder.current.offsetWidth)
        }

        handleResize()
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [numberWidth, displayNameWidth])

    const openLeadTitle = integration?.name ? `${t('open_lead_at')} ${integration.name}` : ''

    return (
        <>
            <div className="call__incoming">
                <div className="call__incoming__header">
                    {incomingCallInsight ? (
                        <>
                            <a
                                href={incomingCallInsightUrl}
                                className="button-integration d-flex align-items-center showIn"
                                rel="noopener noreferrer"
                                target="_blank"
                                title={openLeadTitle}
                            >
                                {integrationFavicons[integration.key]}
                                <h6 className="call__incoming__header--lead ml-1">{incomingCallInsightTitle}</h6>
                                {incomingCallInsightLength > 0 && (
                                    <OpenInNewTabIcon
                                        width="10px"
                                        height="10px"
                                        className="ml-2"
                                        title={openLeadTitle}
                                    />
                                )}
                            </a>
                        </>
                    ) : (
                        <h6 className="call__incoming__header--title">{t('inbound_call')}</h6>
                    )}
                    <div>
                        {incomingCallInsight && <ShowButton onClick={onShowLeadClick} className="showIn" />}
                        <MinimizeButton onClick={onMinimize} />
                    </div>
                </div>
                <div className="call__incoming__body">
                    <CallButton {...buttonsProps} onClick={onAccept} />
                    <div ref={refHolder} className="call__incoming__data">
                        {bothFit ? (
                            <div className="call__incoming__data--row single">
                                {incomingCallDisplayName && <span ref={refDisplayName}>{incomingCallDisplayName}</span>}
                                <span ref={refNumber} className="ltr">
                                    {visualNumberFormat(incomingCallNumber)}
                                </span>
                            </div>
                        ) : (
                            <>
                                {incomingCallDisplayName && (
                                    <div className="call__incoming__data--row">
                                        <Marquee
                                            wrapClass="call__incoming__name"
                                            holderReference={refHolder}
                                            stringReference={refDisplayName}
                                            string={incomingCallDisplayName}
                                        />
                                    </div>
                                )}
                                <div className="call__incoming__data--row">
                                    <Marquee
                                        wrapClass="call__incoming__number ltr"
                                        holderReference={refHolder}
                                        stringReference={refNumber}
                                        string={visualNumberFormat(incomingCallNumber)}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    <CancelCallButton {...buttonsProps} imgHeight="9px" onClick={onReject} />
                </div>
            </div>
        </>
    )
}
